import { useUser } from '@context/user-context';
import type { ActorDetailsFragment } from '@graphql/fragments/actor/core.graphql';
import type { ProfileEssentialDetailsFragment } from '@graphql/fragments/profile/core.graphql';

type WorkspaceRole = ProfileEssentialDetailsFragment['role'];

/** Assert minimum role of actor given array of actor profile workspaces */
export function atLeastRole(profiles: ActorDetailsFragment['profiles'], role: WorkspaceRole): boolean {
  /* 
    Long term, we should make it so the role of the user is easy to obtain via the graph,
    instead of the client pulling down profiles and iterating over them.
  */
  const isTeamOwner = profiles?.some(
    (profile) => profile.workspace?.type === 'TEAM' && profile.role === 'OWNER',
  );

  const isTeamAdmin = profiles?.some(
    (profile) => profile.workspace?.type === 'TEAM' && profile.role === 'ADMIN',
  );

  const isTeamMember = profiles?.some(
    (profile) => profile.workspace?.type === 'TEAM' && profile.role === 'MEMBER',
  );

  switch (role) {
    case 'OWNER':
      return isTeamOwner ?? false;
    case 'ADMIN':
      return (isTeamOwner || isTeamAdmin) ?? false;
    case 'MEMBER':
      return (isTeamOwner || isTeamAdmin || isTeamMember) ?? false;
  }
}

interface UseTeamRoleType {
  loading: boolean;
  /**
   * The current user's role in the workspace.
   */
  role: WorkspaceRole | null;
  /**
   * Assert the current user has at least the given role in the workspace.
   * @param role The role to check against.
   */
  atLeastRole: (role: WorkspaceRole) => boolean;
}

// currently, the dashboard assumes a user can only be apart of one team workspace. If they are apart of multiple, we prioritize the one where they have the highest role
// This is because the workspaceId is taken from the url but the url is unreliable at times.
function useTeamRole(): UseTeamRoleType {
  const { actor, loading } = useUser();

  return {
    role: actor?.profiles?.find((profile) => profile.workspace?.type === 'TEAM')?.role ?? null,
    atLeastRole: (role) => atLeastRole(actor?.profiles, role),
    loading,
  };
}

export { useTeamRole };
